import React from 'react';
import { Table, Layers, Download } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент таблицы покупательной способности по годам
 */
const PurchasingPowerTable = ({ results, formData }) => {
  if (!results || !results.timeSeriesData || results.timeSeriesData.length === 0) {
    return null;
  }

  // Получаем данные для таблицы
  const tableData = [...results.timeSeriesData];
  
  // Экспорт данных в CSV
  const exportToCSV = () => {
    // Определяем заголовки
    const headers = ['Год', 'Инфляция (%)', 'Накопленная инфляция (%)', 'Стоимость (₽)'];
    
    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = tableData.map(item => {
      return [
        item.year,
        formatCsvNumber(item.inflationRate),
        formatCsvNumber(item.cumulativeInflation),
        formatCsvNumber(item.value)
      ].join(';');
    });

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `инфляция_${results.startYear}-${results.endYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Table className="w-5 h-5 mr-2 text-blue-500" />
          Детальный анализ по годам
        </h3>

        <Button
          size="sm"
          onClick={exportToCSV}
          icon={<Download className="w-4 h-4" />}
          variant="outline"
        >
          Скачать данные
        </Button>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[400px]">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[500px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold">Год</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Инфляция за год</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">Накопленная инфляция</th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">
                {results.calculationType === 'appreciation' 
                  ? 'Стоимость с учетом инфляции' 
                  : 'Покупательная способность'}
              </th>
              <th scope="col" className="px-3 py-2 font-semibold text-right">
                {results.calculationType === 'appreciation' 
                  ? 'Рост к начальной сумме' 
                  : 'Снижение к начальной сумме'}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {tableData.map((item, index) => {
              // Расчет процента изменения относительно начальной суммы
              const changeFromInitial = ((item.value / results.initialAmount) - 1) * 100;
              const isPositiveChange = results.calculationType === 'appreciation' ? true : false;

              return (
                <tr
                  key={item.year}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100
                    ${index === 0 ? 'bg-gray-50 dark:bg-gray-700/30' : ''}
                    ${index === tableData.length - 1 ? 'bg-gray-50 dark:bg-gray-700/30 font-medium' : ''}`}
                >
                  <td className="px-3 py-1.5 whitespace-nowrap font-medium">{item.year}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right">
                    {formatNumber(item.inflationRate, 1)}%
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right">
                    {formatNumber(item.cumulativeInflation, 1)}%
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-right">
                    {formatCurrency(item.value)}
                  </td>
                  <td className={`px-3 py-1.5 whitespace-nowrap text-right
                    ${isPositiveChange 
                      ? 'text-green-600 dark:text-green-400' 
                      : 'text-red-600 dark:text-red-400'}`}>
                    {formatNumber(Math.abs(changeFromInitial), 1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        <p>
          При расчете использовались {formData.showCustomRate 
            ? `пользовательская ставка инфляции ${formatNumber(parseFloat(formData.customInflationRate), 1)}%` 
            : 'официальные данные по инфляции в России'}
          {formData.itemCategory !== 'general' 
            ? ` с учетом особенностей категории "${getReadableCategoryName(formData.itemCategory)}"` 
            : ''}.
        </p>
      </div>
    </div>
  );
};

// Вспомогательная функция для преобразования ID категории в читаемое название
const getReadableCategoryName = (categoryId) => {
  const categoryNames = {
    general: "Общая инфляция",
    food: "Продукты питания",
    housing: "Жилье и коммунальные услуги",
    transport: "Транспорт",
    medical: "Медицинские услуги",
    education: "Образование",
    clothing: "Одежда и обувь",
    entertainment: "Развлечения",
    electronics: "Электроника",
    furniture: "Мебель и бытовая техника"
  };
  
  return categoryNames[categoryId] || categoryId;
};

export default PurchasingPowerTable;